import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { collection, onSnapshot, doc, setDoc, deleteDoc, updateDoc, query } from 'firebase/firestore';
import { db, appId, firebaseConfig, rolesConfig } from '../firebase';
import { InputField, Modal } from './SharedComponents';
import { PlusCircle, Loader, Edit, Trash2 } from 'lucide-react';

const UserManagement = ({ currentUser }) => {
    const [users, setUsers] = useState([]);
    const [newUser, setNewUser] = useState({email: '', password: '', role: 'inspector'});
    const [message, setMessage] = useState({text: '', type: ''});
    const [isLoading, setIsLoading] = useState(false);

    // Estados para los modales
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

     useEffect(() => {
        const path = `artifacts/${appId}/public/data/users`;
        const q = query(collection(db, path));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const usersData = [];
            querySnapshot.forEach((doc) => {
                usersData.push({ id: doc.id, ...doc.data() });
            });
            setUsers(usersData);
        });
        return () => unsubscribe();
    }, []);

    const handleChange = (e) => {
        const {name, value} = e.target;
        setNewUser(prev => ({...prev, [name]: value}));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if(!newUser.email || !newUser.password || !newUser.role) {
            setMessage({text: 'Todos los campos son obligatorios.', type: 'error'});
            return;
        }
        setIsLoading(true);
        setMessage({text: '', type: ''});

        try {
            const tempApp = initializeApp(firebaseConfig, `Secondary-${new Date().getTime()}`);
            const tempAuth = getAuth(tempApp);
            const userCredential = await createUserWithEmailAndPassword(tempAuth, newUser.email, newUser.password);
            
            const path = `artifacts/${appId}/public/data/users`;
            await setDoc(doc(db, path, userCredential.user.uid), {
                email: newUser.email,
                role: newUser.role
            });

            setMessage({text: 'Usuario creado exitosamente.', type: 'success'});
            setNewUser({email: '', password: '', role: 'inspector'});

        } catch (error) {
            setMessage({text: 'Error al crear el usuario. El correo ya podría estar en uso.', type: 'error'});
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditClick = (user) => {
        setEditingUser(user);
        setIsEditModalOpen(true);
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();
        if (!editingUser) return;

        try {
            const path = `artifacts/${appId}/public/data/users`;
            const userDocRef = doc(db, path, editingUser.id);
            await updateDoc(userDocRef, { role: editingUser.role });
            setMessage({ text: 'Rol de usuario actualizado.', type: 'success' });
        } catch (error) {
            setMessage({ text: 'Error al actualizar el rol.', type: 'error' });
            console.error(error);
        } finally {
            setIsEditModalOpen(false);
            setEditingUser(null);
        }
    };
    
    const handleDeleteClick = (user) => {
        setUserToDelete(user);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!userToDelete) return;
        try {
            const path = `artifacts/${appId}/public/data/users`;
            await deleteDoc(doc(db, path, userToDelete.id));
            setMessage({ text: `El registro de rol para ${userToDelete.email} ha sido eliminado.`, type: 'success' });
        } catch (error) {
            setMessage({ text: 'Error al eliminar el registro de rol.', type: 'error' });
            console.error(error);
        } finally {
            setIsDeleteModalOpen(false);
            setUserToDelete(null);
        }
    };
    
    const rolesParaCrear = Object.entries(rolesConfig)
      .filter(([key]) => key !== 'propietario')
      .map(([key, value]) => ({ key, name: value.name }));

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
                <h3 className="text-xl font-semibold mb-4">Editar Rol de Usuario</h3>
                {editingUser && (
                     <form onSubmit={handleUpdateUser} className="space-y-4">
                        <InputField label="Correo Electrónico" value={editingUser.email} readOnly disabled />
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                            <select 
                                value={editingUser.role} 
                                onChange={(e) => setEditingUser({...editingUser, role: e.target.value})} 
                                className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                            >
                                {rolesParaCrear.map(role => (
                                   <option key={role.key} value={role.key}>{role.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <button type="button" onClick={() => setIsEditModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300">Cancelar</button>
                            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Guardar Cambios</button>
                        </div>
                    </form>
                )}
            </Modal>

            <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
                 <h3 className="text-xl font-semibold mb-4">Confirmar Eliminación</h3>
                 <p>¿Está seguro de que desea eliminar el registro de rol para <strong>{userToDelete?.email}</strong>? Esta acción no se puede deshacer.</p>
                 <p className="text-sm text-yellow-600 mt-2">Nota: Esto no elimina la cuenta de autenticación del usuario. Debe hacerse manualmente en la Consola de Firebase.</p>
                 <div className="flex justify-end gap-3 mt-6">
                    <button type="button" onClick={() => setIsDeleteModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300">Cancelar</button>
                    <button onClick={confirmDelete} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">Eliminar Registro</button>
                </div>
            </Modal>


            <div>
                <h3 className="text-xl font-semibold mb-4">Crear Nuevo Usuario</h3>
                {message.text && (
                    <div className={`p-3 mb-4 rounded-md text-sm ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {message.text}
                    </div>
                )}
                <form onSubmit={handleSubmit} className="space-y-4">
                    <InputField label="Correo Electrónico" name="email" type="email" value={newUser.email} onChange={handleChange} required />
                    <InputField label="Contraseña" name="password" type="password" value={newUser.password} onChange={handleChange} required />
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                         <select name="role" value={newUser.role} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md shadow-sm">
                            {rolesParaCrear.map(role => (
                               <option key={role.key} value={role.key}>{role.name}</option>
                            ))}
                        </select>
                    </div>
                    <button type="submit" disabled={isLoading} className="w-full flex justify-center items-center gap-2 px-4 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 disabled:bg-blue-300">
                        {isLoading ? <Loader className="animate-spin" /> : <PlusCircle className="w-5 h-5" />} Crear Usuario
                    </button>
                </form>
            </div>
             <div>
                <h3 className="text-xl font-semibold mb-4">Listado de Usuarios</h3>
                <div className="overflow-y-auto max-h-[400px] border rounded-md">
                     <table className="min-w-full bg-white">
                        <thead className="bg-gray-50 sticky top-0">
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Correo Electrónico</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rol</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {users.map(user => {
                                const isProtected = user.id === currentUser?.uid || user.isSuperAdmin === true;
                                let titleMessage = isProtected ? (user.isSuperAdmin ? 'No se puede modificar al Superadministrador' : 'No se puede modificar a sí mismo') : '';

                                return (
                                <tr key={user.id}>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{user.email}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{rolesConfig[user.role]?.name || user.role}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm space-x-2">
                                        <button 
                                            onClick={() => handleEditClick(user)} 
                                            disabled={isProtected}
                                            className="p-1 text-blue-600 hover:text-blue-800 disabled:text-gray-300 disabled:cursor-not-allowed"
                                            title={isProtected ? titleMessage : 'Editar rol'}
                                        >
                                            <Edit size={16}/>
                                        </button>
                                        <button 
                                            onClick={() => handleDeleteClick(user)} 
                                            disabled={isProtected}
                                            className="p-1 text-red-600 hover:text-red-800 disabled:text-gray-300 disabled:cursor-not-allowed"
                                            title={isProtected ? titleMessage : 'Eliminar usuario'}
                                        >
                                            <Trash2 size={16}/>
                                        </button>
                                    </td>
                                </tr>
                            )})}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default UserManagement;